/**
 * Verificación pública de un documento firmado — la página del QR.
 *
 * 🔴 Es la única pantalla del sistema **sin autenticación de ningún tipo**, y
 * eso es el punto: quien la abre es una institución que recibió un certificado
 * laboral y quiere comprobarlo sin tener cuenta en Clobi. Cuando esta ruta no
 * existía, el router caía en el catch-all y mandaba al login — o sea el QR
 * llevaba a una pantalla que le pedía credenciales a un tercero.
 *
 * ⚠️ **No usa `apiClient`.** Ese cliente adjunta el token y, ante un 401,
 * dispara el refresh y la redirección al login: exactamente lo que no puede
 * pasar acá. Se usa `fetch` pelado contra el mismo origen, que además es lo que
 * resuelve el tenant por el host.
 *
 * ⚠️ Y muestra **solo lo que el backend decide mostrar**. La página no compone
 * nada por su cuenta: el RUT llega ya enmascarado y la IP no llega. Si algún
 * día hace falta un dato más, se agrega allá, donde está la decisión.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, BadgeCheck, Download, FileX, Loader2, ShieldX } from 'lucide-react'

interface Verificacion {
  /** `emitido`: lo emitió la empresa y nadie lo firma (el certificado de
   *  antigüedad, #1491). Sin `kind`, es un documento firmado. */
  kind?: 'emitido'
  state: 'valid' | 'revoked' | 'unsigned' | 'rejected'
  document_name: string
  employee_name: string
  signer_name: string
  signer_tax_id_masked: string
  signed_at: string | null
  revoked_at: string | null
  can_download: boolean
}

const ESTADOS = {
  valid: {
    icono: BadgeCheck,
    color: 'text-success-content-soft',
    titulo: 'Documento firmado y vigente',
    detalle: 'La firma electrónica de este documento está registrada en Clobi.',
  },
  revoked: {
    icono: ShieldX,
    color: 'text-danger-content-soft',
    titulo: 'Documento anulado',
    // 🔴 Decir «anulado» y no «inválido»: el documento se firmó de verdad, y lo
    // que ocurrió después es que la empresa lo dejó sin efecto.
    detalle: 'La empresa dejó sin efecto este documento. No debe considerarse vigente.',
  },
  unsigned: {
    icono: AlertTriangle,
    color: 'text-warning-content-soft',
    titulo: 'Documento sin firmar',
    detalle: 'Este documento fue enviado a firma y todavía no se firmó.',
  },
  rejected: {
    icono: ShieldX,
    color: 'text-danger-content-soft',
    titulo: 'Firma rechazada',
    detalle: 'La persona a la que se le pidió la firma no la otorgó.',
  },
} as const

/**
 * Un documento EMITIDO no tiene firma: decir «firmado y vigente» o «Firmado por»
 * afirmaría una firma que no existe. Solo cambian los textos de los dos estados
 * que puede tener.
 */
const ESTADOS_EMITIDO = {
  valid: {
    ...ESTADOS.valid,
    titulo: 'Documento vigente',
    detalle: 'La empresa emitió este documento en Clobi y sigue vigente.',
  },
  revoked: ESTADOS.revoked,
} as const

/** La fecha en la zona horaria de quien mira, que es lo correcto en una web. */
function fechaHora(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function VerificarDocumentoPage() {
  const { token = '' } = useParams()
  const [datos, setDatos] = useState<Verificacion | null>(null)
  const [estado, setEstado] = useState<'cargando' | 'listo' | 'no-existe' | 'error'>('cargando')
  const [bajando, setBajando] = useState(false)

  useEffect(() => {
    let vivo = true
    ;(async () => {
      try {
        const r = await fetch(`/api/v1/verify/${encodeURIComponent(token)}/`)
        if (r.status === 404) { if (vivo) setEstado('no-existe'); return }
        if (!r.ok) { if (vivo) setEstado('error'); return }
        const j = await r.json()
        if (vivo) { setDatos(j); setEstado('listo') }
      } catch {
        if (vivo) setEstado('error')
      }
    })()
    return () => { vivo = false }
  }, [token])

  const descargar = async () => {
    setBajando(true)
    try {
      const r = await fetch(`/api/v1/verify/${encodeURIComponent(token)}/download/`)
      const j = await r.json()
      if (j.url) window.open(j.url, '_blank', 'noopener,noreferrer')
    } catch {
      /* El estado ya se muestra arriba; un fallo de descarga no cambia el veredicto. */
    } finally {
      setBajando(false)
    }
  }

  return (
    <div className="min-h-screen bg-canvas flex items-start justify-center p-4 sm:p-8">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <div className="text-2xl font-bold text-content tracking-tight">clobi</div>
          <div className="text-[10px] text-content-muted tracking-[0.2em] uppercase mt-0.5">
            Verificación de documentos
          </div>
        </div>

        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 sm:p-8 shadow-card">
          {estado === 'cargando' && (
            <div className="py-12 flex justify-center text-content-muted">
              <Loader2 size={24} className="animate-spin" aria-hidden="true" />
            </div>
          )}

          {estado === 'no-existe' && (
            <Aviso
              icono={FileX}
              color="text-content-muted"
              titulo="No se encontró el documento"
              detalle="El código no corresponde a ningún documento de Clobi. Verificar el enlace o volver a escanear el código."
            />
          )}

          {estado === 'error' && (
            <Aviso
              icono={AlertTriangle}
              color="text-warning-content-soft"
              titulo="No se pudo verificar"
              detalle="Hubo un problema al consultar el documento. Intentar de nuevo en unos minutos."
            />
          )}

          {estado === 'listo' && datos && (
            <>
              <Aviso {...(datos.kind === 'emitido'
                ? ESTADOS_EMITIDO[datos.state === 'valid' ? 'valid' : 'revoked']
                : ESTADOS[datos.state])} />

              <dl className="mt-6 space-y-3 text-sm border-t border-divider pt-5">
                <Fila rotulo="Documento" valor={datos.document_name} />
                {datos.kind === 'emitido' && (
                  <Fila rotulo="Persona" valor={datos.employee_name} />
                )}
                <Fila rotulo={datos.kind === 'emitido' ? 'Emitido por' : 'Firmado por'}
                  valor={datos.signer_name || datos.employee_name} />
                {datos.signer_tax_id_masked && (
                  // ⚠️ Llega enmascarado desde el backend. A quien verifica le
                  // alcanza para comparar contra el papel que tiene en mano, y
                  // este enlace es compartible por diseño.
                  <Fila rotulo="Identificación" valor={datos.signer_tax_id_masked} />
                )}
                {datos.signed_at && (
                  <Fila rotulo={datos.kind === 'emitido' ? 'Fecha de emisión' : 'Fecha de firma'}
                    valor={fechaHora(datos.signed_at)} />
                )}
                {datos.revoked_at && (
                  <Fila rotulo="Anulado el" valor={fechaHora(datos.revoked_at)} />
                )}
              </dl>

              {datos.can_download && (
                <button
                  type="button"
                  onClick={descargar}
                  disabled={bajando}
                  className="mt-6 w-full px-4 py-2.5 text-sm rounded-lg bg-primary text-on-primary hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {bajando
                    ? <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                    : <Download size={15} aria-hidden="true" />}
                  Descargar el documento
                </button>
              )}

              <p className="mt-5 text-xs text-content-muted leading-relaxed border-t border-divider pt-4">
                {/* 🔴 La aclaración del tipo de firma va también acá, que es
                    donde un tercero decide si le sirve. Omitirla dejaría que se
                    lea como firma avanzada, que exige otra cosa. */}
                {datos.kind === 'emitido' ? (
                  // Sin firma de nadie: hablar de «firma simple» acá sería afirmar
                  // una firma que no existe (#1491).
                  <>
                    Documento emitido por la empresa con los datos registrados en Clobi.
                    No lleva firma electrónica. Descargar el documento desde esta página
                    permite comprobar que su contenido es el que Clobi tiene registrado.
                  </>
                ) : (
                  <>
                    Firma electrónica <strong>simple</strong> en los términos de la Ley 19.799.
                    No es firma electrónica avanzada. Descargar el documento desde esta página
                    permite comprobar que su contenido es el que Clobi tiene registrado.
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Aviso({ icono: Icono, color, titulo, detalle }: {
  icono: typeof BadgeCheck; color: string; titulo: string; detalle: string
}) {
  return (
    <div className="text-center">
      <Icono size={44} className={`mx-auto ${color}`} aria-hidden="true" />
      <h1 className="mt-3 text-lg font-semibold text-content">{titulo}</h1>
      <p className="mt-1.5 text-sm text-content-muted">{detalle}</p>
    </div>
  )
}

function Fila({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex gap-4">
      <dt className="w-36 shrink-0 text-content-muted">{rotulo}</dt>
      <dd className="text-content break-words">{valor}</dd>
    </div>
  )
}
